// Structural sharing for store reads (perf pass 2026-10-17). Every worker answer arrives as a
// fresh structured clone, so a re-read that found NOTHING new still handed React all-new objects:
// the open conversation re-rendered every visible row (memo compares by identity) on any table
// change — a presence change of someone not even on screen cost 52 row renders.
//
// `share(prev, next)` returns `prev` itself when the two are deep-equal; otherwise a value equal to
// `next` that reuses every subtree of `prev` that did not change. Arrays of records with a string
// `id` (or timeline rows, by their post's id) are matched BY ID — a new newest message shifts every
// index, but the rows are the same rows; other arrays by index. Plain objects, arrays, Maps and Sets are walked; anything else
// (functions, class instances, Dates) compares by identity / Object.is.

type Plain = Record<string, unknown>;

function isPlainObject(v: unknown): v is Plain {
  if (v === null || typeof v !== 'object') return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

/** A record's identity: its own string `id`, or its `post`'s (timeline rows wrap a post). */
function idOf(v: unknown): string | null {
  if (!isPlainObject(v)) return null;
  if (typeof v.id === 'string') return v.id;
  const post = v.post;
  return isPlainObject(post) && typeof post.id === 'string' ? 'post:' + post.id : null;
}

function shareArray(prev: unknown[], next: unknown[]): unknown[] {
  // Match by id when every element of both sides is an identified record (the timeline, users…).
  const keyed = next.length > 0 && prev.length > 0 && next.every((x) => idOf(x) !== null) && prev.every((x) => idOf(x) !== null);
  let byId: Map<string, unknown> | null = null;
  if (keyed) {
    byId = new Map();
    for (const p of prev) byId.set(idOf(p)!, p);
  }
  let same = prev.length === next.length;
  const out = new Array<unknown>(next.length);
  for (let i = 0; i < next.length; i++) {
    const before = byId ? byId.get(idOf(next[i])!) : prev[i];
    const v = before === undefined && byId ? next[i] : share(before, next[i]);
    out[i] = v;
    if (v !== prev[i]) same = false;
  }
  return same ? prev : out;
}

function shareObject(prev: Plain, next: Plain): Plain {
  const nextKeys = Object.keys(next);
  let same = nextKeys.length === Object.keys(prev).length;
  const out: Plain = {};
  for (const k of nextKeys) {
    const v = Object.prototype.hasOwnProperty.call(prev, k) ? share(prev[k], next[k]) : next[k];
    out[k] = v;
    if (v !== prev[k] || !Object.prototype.hasOwnProperty.call(prev, k)) same = false;
  }
  return same ? prev : out;
}

function shareMap(prev: Map<unknown, unknown>, next: Map<unknown, unknown>): Map<unknown, unknown> {
  let same = prev.size === next.size;
  const out = new Map<unknown, unknown>();
  for (const [k, v] of next) {
    const shared = prev.has(k) ? share(prev.get(k), v) : v;
    out.set(k, shared);
    if (!prev.has(k) || shared !== prev.get(k)) same = false;
  }
  return same ? prev : out;
}

function sameSet(prev: Set<unknown>, next: Set<unknown>): boolean {
  if (prev.size !== next.size) return false;
  for (const v of next) if (!prev.has(v)) return false;
  return true;
}

export function share<T>(prev: unknown, next: T): T {
  if (Object.is(prev, next)) return prev as T;
  if (Array.isArray(prev) && Array.isArray(next)) return shareArray(prev, next) as T;
  if (prev instanceof Map && next instanceof Map) return shareMap(prev, next) as T;
  if (prev instanceof Set && next instanceof Set) return (sameSet(prev, next) ? prev : next) as T;
  if (isPlainObject(prev) && isPlainObject(next)) return shareObject(prev, next) as T;
  return next;
}
